import { gql } from '@apollo/client';
import { TOKEN } from 'fragments';

const GET_USER = gql`
  query ($id: ID!) @api(name: user) {
    user(id: $id) {
      id
      email
      first_name
      last_name
      created_at
      avatar
      invited_by_name
      ceo_of
      kyc
      role_id
      role_name
      roles {
        id
        name
      }
      roles_list
    }
  }
`;

const GET_CURRENT_USER = gql`
  query @api(name: user) {
    currentUser {
      id
      email
      first_name
      last_name
      avatar
      kyc
      role_id
      roles {
        id
        name
      }
      role_name
      roles_list
      market_id
    }
  }
`;

const GET_PRO_LOGIN = gql`
  ${TOKEN}
  mutation GetProLogin($input: LoginInput) @api(name: user) {
    login(input: $input) {
      ...TokenFields
      user {
        id
        market_id
        calculator_id
        email
        is_draft
        registration_role
        firstName: first_name
        lastName: last_name
      }
    }
  }
`;

const SEND_INVITE = gql`
  mutation ($input: SendInvitationInput!) @api(name: user) {
    sendInvitation(input: $input) {
      link
      code
      invitation {
        created_at
        expired_at
      }
    }
  }
`;

const ACCEPT_INVITE = gql`
  mutation AcceptInvitation($input: AcceptInvitationInput!) @api(name: user) {
    acceptInvitation(input: $input) {
      invitation {
        first_name
        last_name
      }
      url
    }
  }
`;

const CREATE_NEW_DIGITAL_INDIVIDUAL = gql`
  ${TOKEN}
  mutation CreateNewDigitalIndividual($input: RegisterIndividualInput) @api(name: user) {
    registerIndividual(input: $input) {
      tokens {
        ...TokenFields
        user {
          id
          email
          first_name
          last_name
          market_id
          calculator_id
        }
      }
      status
    }
  }
`;

const GET_URL_FOR_PUBLIC_UPLOAD = gql`
  mutation getPublicUrl($input: sFile) @api(name: user) {
    getUrlForPublicUpload(input: $input) {
      url
      download_url
    }
  }
`;

const GET_IDV_INIT = gql`
  mutation GetIdvInit($input: idvInitInput) @api(name: user) {
    idvInit(input: $input) {
      url
    }
  }
`;

const GET_USER_COMPANIES_LIST = gql`
  query ($user_id: String!) @api(name: market) {
    showAllCompaniesByUserId(user_id: $user_id) {
      id
      user_id
      company_name
      kyb_completed
      tags {
        id
        tag
      }
      logos {
        original_name
        path
        type
      }
    }
  }
`;

const GET_ACTIVE_USER_COMPANIES_LIST = gql`
  query @api(name: market) {
    showActiveUserCompanies {
      id
      user_id
      company_name
      kyb_completed
      logos {
        original_name
        path
        type
      }
    }
  }
`;

const GET_AGREEMENT = gql`
  query @api(name: user) {
    agreementPrivacyAndHonor {
      id
      field
      body
    }
  }
`;

const GET_VALIDATOR_AGREEMENT = gql`
  query @api(name: user) {
    agreementValidator {
      id
      field
      body
    }
  }
`;

const SIGN_VALIDATOR_AGREEMENT = gql`
  query @api(name: user) {
    signAgreementValidator {
      validator_agreement
    }
  }
`;

const GET_ROLE = gql`
  query @api(name: user) {
    roles {
      id
      name
    }
  }
`;

// Admin
const GET_ADMIN_USERS = gql`
  query ($input: UsersInput) @api(name: user) {
    adminUsers(input: $input) {
      count
      users {
        id
        first_name
        last_name
        email
        roles {
          id
          name
        }
        created_at
        invited_by_name
        kyc
        last_login_ip
        status
      }
    }
  }
`;
const GET_ADMIN_ROLES_COUNT = gql`
  query @api(name: user) {
    adminRolesCount {
      role
      count
    }
  }
`;
const UPDATE_USER_ROLE = gql`
  mutation ($input: UpdateUserRoleInput) @api(name: user) {
    updateUserRole(input: $input) {
      success
      user {
        id
        roles {
          name
        }
      }
    }
  }
`;

const UPDATE_USER_STATUS = gql`
  mutation ($input: UpdateUserStatusInput) @api(name: user) {
    updateUserStatus(input: $input) {
      success
      user {
        id
        status
      }
    }
  }
`;
const GET_NOTIFICATIONS = gql`
  query ($input: NotificationsFilterInput!) @api(name: user) {
    notifications(input: $input) {
      id
      read_at
      created_at
      user {
        id
        avatar
        first_name
        last_name
        email
      }
      message
      content {
        id
        body
      }
      title
    }
  }
`;
const MARK_AS_READ_NOTIFICATION = gql`
  mutation ($id: ID!) @api(name: user) {
    markAsReadNotification(id: $id) {
      message
      status
    }
  }
`;
const DELETE_NOTIFICATION = gql`
  mutation ($id: ID!) @api(name: user) {
    deleteNotification(id: $id) {
      message
      status
    }
  }
`;

const FORGOT_USER_PASSWORD = gql`
  mutation ($input: ForgotPasswordInput!) @api(name: user) {
    forgotPassword(input: $input) {
      status
      message
    }
  }
`;

const CONFIRM_FORGOT_USER_PASSWORD = gql`
  mutation ($input: NewPasswordWithCodeInput!) @api(name: user) {
    updateForgottenPassword(input: $input) {
      status
      message
    }
  }
`;

const LOGOUT_USER = gql`
  mutation @api(name: user) {
    logout {
      status
      message
    }
  }
`;

export const GET_SHORT_COMPANY_BY_ID = gql`
  query ($id: String!) @api(name: market) {
    showCompanyById(id: $id) {
      id
      company_name
      logos {
        original_name
        path
        type
      }
    }
  }
`;

export const GET_USER_COMPANIES_SHORT_LIST = gql`
  query ($user_id: String!) @api(name: market) {
    showAllCompaniesByUserId(user_id: $user_id) {
      id
      company_name
      logos {
        original_name
        path
        type
      }
    }
  }
`;

export const CARBON_GET_LIST = gql`
  query GetAllUserCarbonCredit @api(name: market) {
    getAllUserCarbonCredit {
      id
      credit_id
      order_id
      serial_number
      project
      project_link
      smart_contract_address
      user_id
      quantity
      email
      wallet
      created_at
    }
  }
`;

export const CARBON_GET_DATA = gql`
  query GetUserCarbonCredit($id: String!) @api(name: market) {
    getUserCarbonCredit(id: $id) {
      id
      credit_id
      order_id
      serial_number
      project
      project_link
      smart_contract_address
      user_id
      quantity
      email
      wallet
      created_at
    }
  }
`;

export const CARBON_GET_OFFSET_DATA = gql`
  mutation GetAllOffsetData($input: OffsetCreditDataInput!) @api(name: market) {
    getAllOffsetData(input: $input) {
      id
      tx_hash
      user_id
      register_id
      email
      wallet
      parentCertificate {
        smart_contract_address
        project
        project_link
      }
      issued_by
      issued_for
      description
      date
      beneficiary
      calc_result_id
      quantity
    }
  }
`;

export const CARBON_OFFSET = gql`
  mutation Offset($input: OffsetCreditInput!) @api(name: market) {
    offset(input: $input) {
      id
      tx_hash
      user_id
      register_id
      email
      wallet
      parentCertificate {
        smart_contract_address
        project
        project_link
      }
      issued_by
      description
      date
      beneficiary
      calc_result_id
      quantity
    }
  }
`;

// register

export const CARBON_CREATE_REGISTER = gql`
  mutation CreateRegistry($input: RegistryInput!) @api(name: market) {
    createRegistry(input: $input) {
      id
      company_id
      user_id
      name
      description
      unit
      project_id
    }
  }
`;

export const CARBON_GET_REGISTER = gql`
  query GetRegistry($id: String!) @api(name: market) {
    getRegistry(id: $id) {
      id
      company_id
      user_id
      name
      description
      unit
      project_id
    }
  }
`;

export const CARBON_GET_REGISTERS = gql`
  query GetRegistries($company_id: String, $user_id: String) @api(name: market) {
    getRegistries(company_id: $company_id, user_id: $user_id) {
      id
      company_id
      user_id
      name
      description
      unit
      project_id
    }
  }
`;

export const CARBON_UPDATE_REGISTER = gql`
  mutation UpdateRegistry($input: RegistryInput!) @api(name: market) {
    updateRegistry(input: $input) {
      id
      company_id
      user_id
      name
      description
      unit
      project_id
    }
  }
`;

export const CARBON_DELETE_REGISTER = gql`
  mutation DeleteRegistry($id: String!) @api(name: market) {
    deleteRegistry(id: $id) {
      id
    }
  }
`;

export const CARBON_GET_REGISTER_ENTRY = gql`
  query GetRegistryItem($id: String!) @api(name: market) {
    getRegistryItem(id: $id) {
      id
      carbon_registry_id
      customers_id
      name
      email
      wallet_address
      amount
      status
    }
  }
`;

export const CARBON_GET_REGISTER_ENTRIES_BY_ID = gql`
  query GetRegistryItemsByRegistryId($carbon_registry_id: String!) @api(name: market) {
    getRegistryItemsByRegistryId(carbon_registry_id: $carbon_registry_id) {
      id
      carbon_registry_id
      customers_id
      name
      email
      wallet_address
      amount
      status
    }
  }
`;

export const CARBON_CREATE_REGISTER_ENTRY = gql`
  mutation CreateRegistryItem($input: RegistryItemInput!) @api(name: market) {
    createRegistryItem(input: $input) {
      id
      carbon_registry_id
      customers_id
      name
      email
      wallet_address
      amount
      status
    }
  }
`;

export const CARBON_UPDATE_REGISTER_ENTRY = gql`
  mutation UpdateRegistryItem($input: RegistryItemInput!) @api(name: market) {
    updateRegistryItem(input: $input) {
      id
      carbon_registry_id
      customers_id
      name
      email
      wallet_address
      amount
      status
    }
  }
`;

export const CARBON_DELETE_REGISTER_ENTRY = gql`
  mutation DeleteRegistryItem($id: String!) @api(name: market) {
    deleteRegistryItem(id: $id) {
      id
    }
  }
`;

export const REFRESH_TOKEN = gql`
  mutation ($input: RefreshTokenInput) @api(name: user) {
    refreshToken(input: $input) {
      access_token
      refresh_token
      expires_in
      token_type
    }
  }
`;

export const REMOVE_ITEM_FROM_CART = gql`
  mutation ($input: UserCartDestroyInput!) @api(name: market) {
    destroyUserCart(input: $input)
  }
`;

export const SHOW_USER_AVAILABLE_CARTS = gql`
  query ($is_checkout: Boolean) @api(name: market) {
    showAvailableUserCarts(is_checkout: $is_checkout) {
      carts {
        id
        logos {
          original_name
          path
          type
          mediumPath
          smallPath
        }
        company_name
        email
        products {
          id
          company_id
          name
          status
          carts {
            id
            user_id
            is_checkout
            product_id
            quantity
            product_price_id
            availableToOrder
            shopProduct {
              id
              info
              logos {
                path
                mediumPath
                smallPath
                type
              }
              user_id
              name
              company_id
              status
              is_active
              deleted_at
            }
            shopProductPrice {
              id
              product_id
              shopAddress {
                id
                user_id
                user_shop_id
                address_location_id
                is_primary
                is_active
                is_pickup
                is_location_point
                distance
                distance_unit_id
                name
                contact_person
                tel
                info
                delivery_pickup_price
                delivery_price
                currency_id
                schedules {
                  id
                  from
                  to
                  weekDays {
                    id
                    name
                  }
                }
              }
              quantity_all
              min_order_quantity
              price
              productPrice
              productDiscountPrice
              availability
              unit_value
              unit_id
              weight
              weight_unit_id
              unit {
                id
                name
                type
              }
              weightUnit {
                id
                name
                type
              }
            }
          }
        }
      }
      groupedByDeliveries {
        id
        logos {
          original_name
          path
          type
          mediumPath
          smallPath
        }
        company_name
        email
        products {
          id
          company_id
          name
          status
          carts {
            id
            user_id
            product_id
            quantity
            product_price_id
            availableToOrder
            shopProduct {
              id
              info
              logos {
                path
                mediumPath
                smallPath
                type
              }
              user_id
              name
              company_id
              status
              is_active
              deleted_at
            }
            shopProductPrice {
              id
              product_id
              shopAddress {
                id
                user_id
                user_shop_id
                address_location_id
                is_primary
                is_active
                is_pickup
                is_location_point
                distance
                distance_unit_id
                name
                contact_person
                tel
                info
                schedules {
                  id
                  from
                  to
                  weekDays {
                    id
                    name
                  }
                }
                delivery_pickup_price
                delivery_price
                currency_id
              }
              quantity_all
              min_order_quantity
              price
              productPrice
              productDiscountPrice
              availability
              unit_value
              unit_id
              weight
              weight_unit_id
              unit {
                id
                name
                type
              }
              weightUnit {
                id
                name
                type
              }
            }
          }
        }
      }
    }
  }
`;

export {
  GET_USER,
  LOGOUT_USER,
  GET_CURRENT_USER,
  GET_PRO_LOGIN,
  SEND_INVITE,
  CREATE_NEW_DIGITAL_INDIVIDUAL,
  GET_IDV_INIT,
  GET_USER_COMPANIES_LIST,
  GET_ACTIVE_USER_COMPANIES_LIST,
  GET_AGREEMENT,
  GET_ROLE,
  GET_ADMIN_USERS,
  GET_ADMIN_ROLES_COUNT,
  UPDATE_USER_ROLE,
  UPDATE_USER_STATUS,
  MARK_AS_READ_NOTIFICATION,
  DELETE_NOTIFICATION,
  GET_URL_FOR_PUBLIC_UPLOAD,
  FORGOT_USER_PASSWORD,
  CONFIRM_FORGOT_USER_PASSWORD,
  GET_VALIDATOR_AGREEMENT,
  SIGN_VALIDATOR_AGREEMENT,
  ACCEPT_INVITE,
  GET_NOTIFICATIONS,
};
